import { Injectable, Inject, LOCALE_ID } from '@angular/core';
import { TSDataSorted, PlotEvent, Coherence, NdviDataMultiple, PlotMonitors, WeatherData } from '../model/plot';
import { Monitor } from "../model/assessment";
import { colorCodes }  from '../constants';
import { UtilService } from './util.service';
import * as d3 from "d3";

export interface TSDataUnsorted {
  ndviData: NdviDataMultiple,
  lockoutMonitor: Monitor | undefined,
  plotMonitors: PlotMonitors | undefined,
  allEvents: PlotEvent[] | null,
  allWeather: WeatherData[] | null
}
export interface ChartObject {
  date: Date;
  value: number;
}

@Injectable({
  providedIn: 'root'
})
export class ChartService {

  constructor(private util: UtilService, 
    @Inject(LOCALE_ID) private locale: string) { }


   private getInterpolatedValue(date: Date, interpolatedData: ChartObject[]) {
    return interpolatedData.find(data => data.date.getTime() === date.getTime());
  }

  async interpolateChartData(planetNdvi: ChartObject[], sentinelNdvi: ChartObject[],
    coherences: Coherence[],
    precipitation: ChartObject[], temperature: ChartObject[]) {
      const interpolatedData: TSDataSorted[] = [];
      const interpolatedPlanetNdvi = planetNdvi.length > 0 ? await this.util.interpolateDates(planetNdvi) : [];
      const interpolatedSentinelNdvi = sentinelNdvi.length > 0 ? await this.util.interpolateDates(sentinelNdvi) : [];
      const interpolatedTemperature = temperature.length > 0 ? await this.util.interpolateDates(temperature) : [];
     
      const coherencesInterpolated: {orbit: string, polarisation: 'VH' | 'VV', values: ChartObject[]}[] = [];
      for (const coherenceCategory of coherences) {
          const copyOfCategory = Object.assign({}, coherenceCategory);
          copyOfCategory.values = await this.util.interpolateDates(coherenceCategory.values);
          coherencesInterpolated.push(copyOfCategory);
        }

      const dates = sentinelNdvi.map(entry => entry.date)
          .concat(planetNdvi.map(entry => entry.date))
          .concat(precipitation.map(entry => new Date(entry.date)))
          .concat(temperature.map(entry => new Date(entry.date)));

      const currentYear = this.util.getMostFrequentYear(dates);
      const allDates = d3.timeDays(
        new Date(currentYear - 1 +'-12-31'),
        new Date(currentYear + '-11-31')
      );

      for (const date of allDates) {
        const interpolatedSentinelValue = this.getInterpolatedValue(date, interpolatedSentinelNdvi);
        const interpolatedPlanetValue = this.getInterpolatedValue(date, interpolatedPlanetNdvi);
        const interpolatedTemperatureValue = this.getInterpolatedValue(date, interpolatedTemperature);
        const interpolatedPrecipitationValue = precipitation.find(item => item.date.getDate() === date.getDate() && 
          item.date.getMonth() === date.getMonth() && item.date.getFullYear() === date.getFullYear()) || null;

        const interpolatedCoherencesValue: {orbit: string, polarisation: "VH" | "VV", value: number}[] = [];
        coherencesInterpolated.forEach(coherenceCategory => {
          coherenceCategory.values.forEach(value => {
            if (value.date.getTime() === date.getTime()) {
              interpolatedCoherencesValue.push({orbit: coherenceCategory.orbit, polarisation: coherenceCategory.polarisation, value: value.value});
            }
          });
        });

        interpolatedData.push({
          date,
          ndviSentinel: interpolatedSentinelValue ? interpolatedSentinelValue.value : null,
          ndviPlanet: interpolatedPlanetValue ? interpolatedPlanetValue.value : null,
          coherences: interpolatedCoherencesValue.length > 0 ? interpolatedCoherencesValue : undefined,
          temperature: interpolatedTemperatureValue ? interpolatedTemperatureValue.value : null,
          precipitation: interpolatedPrecipitationValue ? interpolatedPrecipitationValue.value : null
        });
      }

      return { currentYear, interpolatedData };

    }

  async createChartData(data: TSDataSorted[], sentinelOriginal:ChartObject[],
    planetOriginal:ChartObject[], orbits: string[] | undefined, coherences: Coherence[] | undefined,
    lockout: Monitor | undefined, events: PlotEvent[], activePhotoDate: Date | undefined, activePhotoSource: string | undefined) {

    let datasets: any[] = [];
    let labels: any[] = [];
    let datesLabels: any[] = [];
    let allAnnotations: any = {};

    if (data.length > 0) {
      labels = data.map(dataSet => dataSet.date.getTime());
      datesLabels = data.map(dataSet => dataSet.date);

      if (data.filter(item => item.ndviSentinel !== null).length > 0) {
        datasets = datasets.concat({
          label: 'NDVI Sentinel',
          data: data.map(dataSet => dataSet.ndviSentinel),
          fill: false,
          borderColor: colorCodes.sentinel_data,
          tension: 0.1,
          pointRadius: 0.05,
          borderWidth: 2,
        });

      }
      if (data.filter(item => item.ndviPlanet !== null).length > 0) {
        datasets = datasets.concat({
          label: 'NDVI Planet',
          data: data.map(dataSet => dataSet.ndviPlanet),
          fill: false,
          borderColor: colorCodes.planet_data,
          tension: 0.1,
          pointRadius: 0.05,
          borderWidth: 2,
        });
      }

      let intensity = 50;
      if (orbits) {
        orbits.forEach(orbit => {
          datasets.push({
            label: `VH COH ${orbit}`,
            data: data.map(dataSet => {
              const coherence = dataSet.coherences?.find(entry => entry.orbit === orbit && entry.polarisation === 'VH');
              return coherence?.value;
            }),
            fill: false,
            borderColor: `#${intensity.toString(16)}${intensity.toString(16)}${intensity.toString(16)}`,
            tension: 0.1,
            pointRadius: 0.05,
            borderWidth: 2,
            borderDash: [10, 4],
          });
          intensity += 50;
          // datasets.push({
          //   label: `COH ${orbit} VV`,
          //   data: data.map(dataSet => {
          //     const coherence = dataSet.coherences?.find(entry => entry.orbit === orbit && entry.polarisation === 'VV');
          //     return coherence?.value;
          //   }),
          //   fill: false,
          //   borderColor: `#${intensity.toString(16)}${intensity.toString(16)}${intensity.toString(16)}`,
          //   tension: 0.1,
          //   pointRadius: 0.05,
          //   borderWidth: 2,
          //   borderDash: [10, 4],
          // });
          // intensity += 30;
        });

        if (coherences) {
          orbits.forEach((orbit: string) => {
            const vhCoherences = coherences.find(coherenceCategory => coherenceCategory.orbit === orbit && coherenceCategory.polarisation === 'VH')?.values;
            // const vvCoherences = coherences.find(coherenceCategory => coherenceCategory.orbit === orbit && coherenceCategory.polarisation === 'VV')?.values;
    
            if (vhCoherences && vhCoherences.length > 0) {
              for (const [i, item] of vhCoherences.entries()) {
                if (item.value !== null) {
                  allAnnotations[`VH COH ${orbit} ` + i.toString] = {
                    drawTime: 'afterDraw',
                    type: 'point',
                    xValue: item.date.getTime(),
                    yValue: item.value,
                    backgroundColor: `#${intensity.toString(16)}${intensity.toString(16)}${intensity.toString(16)}`,
                    borderColor: `#${intensity.toString(16)}${intensity.toString(16)}${intensity.toString(16)}`,
                    radius: 2
                  }
                }
              }
            }
            intensity += 50;
            // if (vvCoherences && vvCoherences.length > 0) {
            //   for (const [i, item] of vvCoherences.entries()) {
            //     if (item.value !== null) {
            //       allAnnotations[`VV COH ${orbit} ` + i] = {
            //         drawTime: 'afterDraw',
            //         type: 'point',
            //         xValue: item.date.getTime(),
            //         yValue: item.value,
            //         backgroundColor: `#${intensity.toString(16)}${intensity.toString(16)}${intensity.toString(16)}`,
            //         borderColor: `#${intensity.toString(16)}${intensity.toString(16)}${intensity.toString(16)}`,
            //         radius: 2,
            //         // display: () => this.pointVisible('Coherence VV')
            //       }
            //     }
            //   }
            // }
            // intensity += 30;
          });
        }
 
      }

      // add Sperrzeitraum
      if (lockout?.dateEndParsed && lockout?.dateStartParsed) {
        allAnnotations['lockout'] = {
          drawTime: 'beforeDraw',
          type: 'box',
          xMin: lockout.dateStartParsed.getTime(),
          xMax: lockout.dateEndParsed.getTime(),
          yMin: 0.0,
          yMax: 1.0,
          backgroundColor: colorCodes.lockout_data,
          borderColor: colorCodes.lockout_data,
        }
      }

      // add arbitrary lines on 15. of the month because irregular grid doesn't work
      for (const [i, label] of datesLabels.entries()) {
        if (label.getDate() === 1) {
          allAnnotations['grid' + i] = {
            type: 'line',
            xMin: label.getTime(),
            xMax: label.getTime(),
            borderColor: colorCodes.grid_line_start,
            borderWidth: 1,
            drawTime: "beforeDatasetsDraw"
          };
        }
        if (label.getDate() === 15) {
          allAnnotations['grid' + i] = {
            type: 'line',
            xMin: label.getTime(),
            xMax: label.getTime(),
            borderColor: colorCodes.grid_line_middle,
            borderWidth: 1,
            drawTime: "beforeDatasetsDraw"
          };
        }
      }

      // add event lines
      if (events.length > 0) {
        for (const [i, item] of events.entries()) {
          if (item.date) {
            const color = item.valid ? colorCodes.event_valid : colorCodes.event_invalid;
            const labelColor = item.valid ? colorCodes.event_label_valid : colorCodes.event_label_invalid;
            const zValue = item.valid ? 2 : 1;
            allAnnotations['Event ' + item.source + ' ' + item.date.getTime()] = {
              type: 'line',
              xMin: item.date.getTime(),
              xMax: item.date.getTime(),
              borderColor: color,
              borderWidth: 2,
              borderDash: [10, 4],
              label: {
                content: item.date.toLocaleDateString(this.locale, { day: "numeric", month: "numeric" }),
                display: true,
                position: "end",
                rotation: 0,
                backgroundColor: labelColor,
                shadowBlur: 50,
                z: zValue,
                font: {
                  size: 11,
                  weight: 'lighter',
                  lineHeight: 1.0
                }
              }
            };
          }
        }
      }

      // show all real points in the dataset
      if (sentinelOriginal.length > 0) {
        for (const [i, item] of sentinelOriginal.entries()) {
          if (item.value !== null) {
            allAnnotations['NDVI Sentinel ' + i] = {
              // drawTime: 'afterDraw',
              type: 'point',
              xValue: item.date.getTime(),
              yValue: item.value,
              backgroundColor: colorCodes.sentinel_data,
              borderColor: colorCodes.sentinel_data,
              radius: 2
            }
          }
        }
      }
      if (planetOriginal.length > 0) {
        for (const [i, item] of planetOriginal.entries()) {
          if (item.value !== null) {
            allAnnotations['NDVI Planet ' + i] = {
              // drawTime: 'afterDraw',
              type: 'point',
              xValue: item.date.getTime(),
              yValue: item.value,
              backgroundColor: colorCodes.planet_data,
              borderColor: colorCodes.planet_data,
              radius: 2
            }
          }
        }
      }

      if (data.filter(item => item.precipitation !== null).length > 0){
        datasets = datasets.concat({
          label: $localize`:@@precipitation:Precipitation`,
          type: 'bar',
          data: data.map(dataSet => dataSet.precipitation),
          borderColor: colorCodes.precipitation,
          backgroundColor: colorCodes.precipitation,
          yAxisID: 'y1',
        });
      }

      if (data.filter(item => item.temperature !== null).length > 0) {
        datasets = datasets.concat({
          label: $localize`:@@temperature:Temperature`,
          data: data.map(dataSet => dataSet.temperature),
          fill: false,
          borderColor: colorCodes.temperature,
          tension: 0.1,
          pointRadius: 0.05,
          borderWidth: 2,
          yAxisID: 'y1',
        });
      }

      if (activePhotoDate && activePhotoSource) {
        allAnnotations['activePhoto'] = await this.createPhotoAnnotation(activePhotoDate, activePhotoSource);

        const label = $localize`:@@photo:Photo`;
        datasets.push({
          type: 'line',
          label,
          fill: false,
          borderColor: activePhotoSource.toLowerCase().indexOf('sfb') !== -1 ? colorCodes.sfb_data : colorCodes.ast_data,
          tension: 0.1,
          pointRadius: 0.05,
          borderWidth: 2,
          borderDash: [10, 4],
          data: []
        })
      }
    }
    return { labels, datasets, allAnnotations };
  }


  async createPhotoAnnotation(date: Date, activePhotoSource: string | undefined) {
    const color = activePhotoSource && activePhotoSource.toLowerCase().indexOf('sfb') !== -1 ? colorCodes.sfb_data : colorCodes.ast_data;
    const labelColor = activePhotoSource && activePhotoSource.toLowerCase().indexOf('sfb') !== -1 ? colorCodes.sfb_data : colorCodes.ast_data;
    const zValue = 2;
    return {
      type: 'line',
      xMin: date.getTime(),
      xMax: date.getTime(),
      borderColor: color,
      borderWidth: 2,
      borderDash: [10, 4],
      label: {
        content: date.toLocaleDateString(this.locale, { day: "numeric", month: "numeric" }),
        display: true,
        position: "end",
        rotation: 0,
        backgroundColor: labelColor,
        shadowBlur: 50,
        z: zValue,
        font: {
          size: 11,
          weight: 'lighter',
          lineHeight: 1.0
        }
      }
    };
  }
}
